import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tag,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { BodyOf, ResponseOf } from "../../../core/api";
import useApi from "../../../shared/hooks/useApi";
import { PatientServicePlanId } from "../../../shared/schema/schema";
import { formatErrorResponse } from "../../../shared/utils/format-response-error";

type ServicePlan = ResponseOf<"get", "./service_plans/:patientServicePlanId">;
type SignatureClaim = ServicePlan["signatureClaims"][number];
type SignatureRole = BodyOf<
  "post",
  "./service_plans/:patientServicePlanId/signature_claims"
>["role"];

/**
 * Only what this button reads, so callers holding a GraphQL row of the same
 * claim (the agency-wide dashboard) can pass it without selecting every field.
 */
type ClaimSummary = Pick<SignatureClaim, "recipientEmail" | "status">;

interface Props {
  claim: ClaimSummary | undefined;
  isDisabled: boolean;
  role: SignatureRole;
  servicePlanId: PatientServicePlanId;
  title: string;

  /** Extra invalidation for callers whose list is not the plan-by-id query. */
  onSent?: () => void;
}

export default function ClaimSignatureByEmailButton({
  claim,
  isDisabled,
  role,
  servicePlanId,
  title,
  onSent,
}: Props) {
  const { queries, mutations } = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [email, setEmail] = useState(claim?.recipientEmail ?? "");

  const sendMutation = useMutation({
    ...mutations.servicePlan.sendSignatureClaim(servicePlanId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queries.servicePlan.byId(servicePlanId).queryKey });
      onSent?.();
      onClose();
      toast({
        title: `Signing link sent for ${title}`,
        status: "success",
        position: "top-right",
        isClosable: true,
      });
    },
    onError: (error: unknown) => {
      toast({
        title: "Could not send the signing link",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
      });
    },
  });

  // Loose on purpose: the server is the authority on deliverability, and a
  // stricter regex here mostly rejects addresses that are actually valid.
  const isEmailValid = /^\S+@\S+\.\S+$/.test(email.trim());

  return (
    <>
      {claim !== undefined && <ClaimStatusTag status={claim.status} />}
      <Button
        isDisabled={isDisabled}
        size="sm"
        variant="outline"
        onClick={() => {
          setEmail(claim?.recipientEmail ?? "");
          onOpen();
        }}
      >
        {claim === undefined ? "Claim by email" : "Resend link"}
      </Button>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Claim {title} signature by email</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Text color="gray.600" fontSize="sm">
                We will email a one-time link that lets the signee enter their name, date and
                signature. The link works once and expires in 14 days.
              </Text>
              {claim !== undefined && (
                <Text color="orange.600" fontSize="sm">
                  Sending a new link will stop the previous one from working.
                </Text>
              )}
              <FormControl>
                <FormLabel fontSize="sm">Email address</FormLabel>
                <Input
                  placeholder="name@example.com"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button marginRight={3} variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              isDisabled={!isEmailValid}
              isLoading={sendMutation.isPending}
              onClick={() => sendMutation.mutate({ role, recipientEmail: email.trim() })}
            >
              Send link
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

function ClaimStatusTag(props: { status: SignatureClaim["status"] }) {
  switch (props.status) {
    case "AWAITING":
      return <Tag colorScheme="gray">Awaiting</Tag>;
    case "OPENED":
      return <Tag colorScheme="blue">Opened</Tag>;
    case "CLAIMED":
      return <Tag colorScheme="green">Claimed</Tag>;
    case "EXPIRED":
      return <Tag colorScheme="red">Expired</Tag>;
  }
}
